'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import type { ComponentType, MouseEvent, ReactNode } from 'react'
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'

import { Button, ButtonLink } from '@/components/ui/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/components/ui/dialog'
import { Document } from '@/components/ui/document'
import type { LegalPageKey } from '@/content/legal'
import type { Locale } from '@/i18n/routing'
import { legalDialogKeys } from './legal-keys'

type LegalDialogContextValue = {
  openLegal: (key: LegalPageKey) => void
  /** Fetches a document's module ahead of `openLegal`, so the dialog opens onto the text. */
  preloadLegal: (key: LegalPageKey) => void
  close: () => void
}

type LegalDialogProviderProps = {
  locale: Locale
  labels: Record<string, string>
  actions: {
    openInNewTab: string
    close: string
  }
  links: Record<LegalPageKey, string>
  children: ReactNode
}

const LegalDialogContext = createContext<LegalDialogContextValue | null>(null)

type LegalContentModule = { default: ComponentType }
type LegalImporter = () => Promise<LegalContentModule>

const LegalDialogLoader = () => (
  <div className="flex min-h-40 items-center justify-center" data-sentry-component="LegalDialogLoader" data-sentry-source-file="legal-dialog-provider.tsx">
    <div
      className="size-7 animate-spin rounded-full border-2 border-mist-300 border-t-mist-950 dark:border-mist-700 dark:border-t-white"
      aria-hidden="true"
    />
    <span className="sr-only">Loading legal document</span>
  </div>
)

const normalizePathname = (href: string) => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const url = new URL(href, window.location.origin)
    return url.pathname.replace(/\/$/, '') || '/'
  } catch {
    return null
  }
}

/**
 * The documents, fetched on demand — through a plain `import()`, deliberately not `next/dynamic`.
 *
 * `next/dynamic` with a `loading` component is a Suspense boundary, and a Suspense boundary that
 * resolves inside the open dialog is a reveal React answers, on a page with a `<ViewTransition>`,
 * by snapshotting the whole document and crossfading it. The swap from spinner to text then
 * played as the dialog closing and another one opening. Loading the module by hand and rendering
 * the spinner from state means nothing suspends and nothing animates but the dialog itself.
 */
const legalImportersByLocale: Record<Locale, Record<LegalPageKey, LegalImporter>> = {
  nl: {
    'legal-terms': () => import('@/content/legal/nl/legal-terms.mdx'),
    'legal-privacy': () => import('@/content/legal/nl/legal-privacy.mdx'),
    'legal-cookie': () => import('@/content/legal/nl/legal-cookie.mdx'),
    'legal-security': () => import('@/content/legal/nl/legal-security.mdx'),
    'legal-api-terms': () => import('@/content/legal/nl/legal-api-terms.mdx'),
    'legal-dpa': () => import('@/content/legal/nl/legal-dpa.mdx'),
    'legal-support-service': () => import('@/content/legal/nl/legal-support-service.mdx'),
    'legal-responsible-disclosure': () => import('@/content/legal/nl/legal-responsible-disclosure.mdx'),
    'legal-subprocessors': () => import('@/content/legal/nl/legal-subprocessors.mdx'),
    'legal-acceptable-use': () => import('@/content/legal/nl/legal-acceptable-use.mdx'),
  },
  en: {
    'legal-terms': () => import('@/content/legal/en/legal-terms.mdx'),
    'legal-privacy': () => import('@/content/legal/en/legal-privacy.mdx'),
    'legal-cookie': () => import('@/content/legal/en/legal-cookie.mdx'),
    'legal-security': () => import('@/content/legal/en/legal-security.mdx'),
    'legal-api-terms': () => import('@/content/legal/en/legal-api-terms.mdx'),
    'legal-dpa': () => import('@/content/legal/en/legal-dpa.mdx'),
    'legal-support-service': () => import('@/content/legal/en/legal-support-service.mdx'),
    'legal-responsible-disclosure': () => import('@/content/legal/en/legal-responsible-disclosure.mdx'),
    'legal-subprocessors': () => import('@/content/legal/en/legal-subprocessors.mdx'),
    'legal-acceptable-use': () => import('@/content/legal/en/legal-acceptable-use.mdx'),
  },
}

/** Module-level, so a document fetched once stays fetched across navigations and dialogs. */
const loadedContent = new Map<string, ComponentType>()
const loadingContent = new Map<string, Promise<ComponentType>>()

const contentId = (locale: Locale, key: LegalPageKey) => `${locale}:${key}`

function loadContent(locale: Locale, key: LegalPageKey): Promise<ComponentType> {
  const id = contentId(locale, key)
  const loaded = loadedContent.get(id)
  if (loaded) {
    return Promise.resolve(loaded)
  }

  const loading = loadingContent.get(id)
  if (loading) {
    return loading
  }

  const promise = legalImportersByLocale[locale][key]()
    .then((module) => {
      loadedContent.set(id, module.default)
      return module.default
    })
    .finally(() => {
      loadingContent.delete(id)
    })
  loadingContent.set(id, promise)
  return promise
}

export function LegalDialogProvider({ locale, labels, actions, links, children }: LegalDialogProviderProps) {
  const [activeKey, setActiveKey] = useState<LegalPageKey | null>(null)
  // Bumped when a document arrives, so the open dialog re-renders from the spinner to the text.
  const [, contentArrived] = useReducer((count: number) => count + 1, 0)

  const openLegal = useCallback((key: LegalPageKey) => setActiveKey(key), [])
  const close = useCallback(() => setActiveKey(null), [])
  const preloadLegal = useCallback(
    (key: LegalPageKey) => {
      // A failed fetch is retried on open; nothing to report from a hover.
      loadContent(locale, key).catch(() => {})
    },
    [locale],
  )

  useEffect(() => {
    if (!activeKey || loadedContent.has(contentId(locale, activeKey))) {
      return
    }

    let current = true
    loadContent(locale, activeKey)
      .then(() => {
        if (current) {
          contentArrived()
        }
      })
      .catch(() => {})
    return () => {
      current = false
    }
  }, [locale, activeKey])

  const legalPathToKey = useMemo(() => {
    const map: Record<string, LegalPageKey> = {}
    for (const key of legalDialogKeys) {
      const href = links[key]
      if (typeof href !== 'string') {
        continue
      }
      const normalized = href.replace(/\/$/, '') || '/'
      map[normalized] = key
    }
    return map
  }, [links])

  const keyForAnchor = useCallback(
    (target: EventTarget | null): LegalPageKey | null => {
      if (!(target instanceof Element)) {
        return null
      }

      const anchor = target.closest('a')
      if (!anchor) {
        return null
      }

      if ((anchor.getAttribute('target') ?? '').toLowerCase() === '_blank' || anchor.hasAttribute('download')) {
        return null
      }

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) {
        return null
      }

      const pathname = normalizePathname(href)
      if (!pathname) {
        return null
      }

      return legalPathToKey[pathname] ?? null
    },
    [legalPathToKey],
  )

  const handleDialogLinkClickCapture = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!activeKey || event.defaultPrevented || event.button !== 0) {
        return
      }

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }

      const nextKey = keyForAnchor(event.target)
      if (!nextKey) {
        return
      }

      event.preventDefault()
      setActiveKey(nextKey)
    },
    [activeKey, keyForAnchor],
  )

  // A document linked from inside another is fetched while the pointer is on the link, the same
  // as the forms do for the links that open the dialog.
  const handleDialogLinkHoverCapture = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const nextKey = keyForAnchor(event.target)
      if (nextKey) {
        preloadLegal(nextKey)
      }
    },
    [keyForAnchor, preloadLegal],
  )

  // Looked up, not created, so the lint rule about components made during render does not apply —
  // which is also why it is rendered with `createElement` rather than as JSX.
  const content = activeKey ? (loadedContent.get(contentId(locale, activeKey)) ?? null) : null
  const title = activeKey ? labels[activeKey] : ''
  const href = activeKey ? links[activeKey] : '#'

  const value = useMemo(() => ({ openLegal, preloadLegal, close }), [openLegal, preloadLegal, close])

  return (
    <LegalDialogContext.Provider value={value} data-sentry-element="LegalDialogContext.Provider" data-sentry-component="LegalDialogProvider" data-sentry-source-file="legal-dialog-provider.tsx">
      {children}
      <Dialog open={Boolean(activeKey)} onClose={close} size="4xl" data-sentry-element="Dialog" data-sentry-source-file="legal-dialog-provider.tsx">
        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="legal-dialog-provider.tsx">{title}</DialogTitle>
        <DialogBody onClickCapture={handleDialogLinkClickCapture} onMouseOverCapture={handleDialogLinkHoverCapture} data-sentry-element="DialogBody" data-sentry-source-file="legal-dialog-provider.tsx">
          {/* `Document` leaves heading sizes to its consumer — see the note there. */}
          {activeKey ? (
            content ? (
              <Document className="[&_h2]:text-base/8">{createElement(content)}</Document>
            ) : (
              <LegalDialogLoader />
            )
          ) : null}
        </DialogBody>
        <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="legal-dialog-provider.tsx">
          {activeKey ? (
            <ButtonLink href={href} target="_blank" rel="noreferrer noopener" size="lg" color="light">
              {actions.openInNewTab}
            </ButtonLink>
          ) : null}
          <Button type="button" size="lg" color="dark/light" onClick={close} data-sentry-element="Button" data-sentry-source-file="legal-dialog-provider.tsx">
            {actions.close}
          </Button>
        </DialogActions>
      </Dialog>
    </LegalDialogContext.Provider>
  )
}

export function useLegalDialog() {
  const context = useContext(LegalDialogContext)
  if (!context) {
    throw new Error('useLegalDialog must be used within LegalDialogProvider')
  }
  return context
}
